import { GitBranch, Merge, ShieldAlert } from "lucide-react";
import { Component, lazy, Suspense, useMemo, type CSSProperties, type ErrorInfo, type ReactNode } from "react";
import { graphDataForMode } from "../researchData";
import type { GraphMode, NarratorRecord } from "../types";
import { EvidenceInspector } from "./EvidenceInspector";

const GraphCanvas3D = lazy(() => import("./GraphCanvas3D"));

type Props = {
  mode: GraphMode;
  onModeChange: (mode: GraphMode) => void;
  selected: NarratorRecord;
  onSelect: (person: NarratorRecord) => void;
};

const modeMeta = {
  transmission: { label: "Transmission", Icon: GitBranch, color: "#79a7b9" },
  convergence: { label: "Convergence", Icon: Merge, color: "#c6a86a" },
  contradiction: { label: "Contradictions", Icon: ShieldAlert, color: "#a8544f" },
};

function GraphFallback({ failed = false }: { failed?: boolean }) {
  return (
    <div className="graph-fallback" role="status">
      <div className="fallback-orbit" aria-hidden="true">
        {Array.from({ length: 12 }, (_, index) => <i key={index} style={{ "--node-index": index } as CSSProperties} />)}
      </div>
      <span>{failed ? "3D view unavailable — the evidence index remains available below." : "Loading the 3D source map…"}</span>
    </div>
  );
}

class GraphBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn("The optional 3D graph could not start.", error, info.componentStack);
  }

  render() {
    return this.state.failed ? <GraphFallback failed /> : this.props.children;
  }
}

export function GraphStage({ mode, onModeChange, selected, onSelect }: Props) {
  const data = useMemo(() => graphDataForMode(mode), [mode]);
  const selectedConnections = data.links.filter((link) => {
    const source = typeof link.source === "string" ? link.source : link.source.id;
    const target = typeof link.target === "string" ? link.target : link.target.id;
    return source === selected.id || target === selected.id;
  }).length;

  return (
    <section className="graph-shell" id="graph">
      <div className="hero-copy">
        <h1>A history built from claims, not stories.</h1>
        <p>Trace every assertion to its earliest recoverable layer, transmission route, variant, and counterclaim.</p>
        <div className="hero-actions">
          <button className="button-primary" onClick={() => document.querySelector(".graph-viewport")?.scrollIntoView({ behavior: "smooth", block: "center" })}>Explore the graph</button>
          <button className="button-secondary" onClick={() => document.getElementById("events")?.scrollIntoView({ behavior: "smooth" })}>Browse events</button>
        </div>
        <div className="mode-switch" aria-label="Graph mode">
          {(Object.keys(modeMeta) as GraphMode[]).map((key) => {
            const { label, Icon } = modeMeta[key];
            return <button className={mode === key ? "is-active" : ""} onClick={() => onModeChange(key)} key={key}><Icon size={15} />{label}</button>;
          })}
        </div>
      </div>
      <div className="graph-viewport">
        <div className="graph-grid" />
        <GraphBoundary>
          <Suspense fallback={<GraphFallback />}>
            <GraphCanvas3D mode={mode} data={data} onSelect={onSelect} />
          </Suspense>
        </GraphBoundary>
        <div className="graph-legend">
          <span><i className="dot tier-one" />Priority source</span>
          <span><i className="dot tier-two" />Claim-critical voice</span>
          <span><i className="dot tier-three" />Later bridge</span>
        </div>
      </div>
      <EvidenceInspector person={selected} connectionCount={selectedConnections} />
    </section>
  );
}

export default GraphStage;
